/**
 * Shared API client utilities.
 *
 * authFetch() works like fetch() but automatically attaches the Cognito
 * access token as an Authorization: Bearer header when the user is signed in.
 *
 * Token resolution order:
 *  1. Call fetchAuthSession() — Amplify returns the cached in-memory token if
 *     still valid, or silently refreshes it via the refresh token if expired.
 *     This ensures that background tabs (where the access token may have expired
 *     while the tab was frozen) always attach a fresh token on the next request.
 *  2. Fall back to the module-level _token if fetchAuthSession throws (user is
 *     genuinely logged out, Amplify not yet configured, etc.).
 *
 * AuthContext mirrors its accessToken state here via setAuthToken() so that
 * React components that render the token (e.g. for display) stay in sync.
 */

import { fetchAuthSession } from '@aws-amplify/auth'

let _token: string | null = null

export function setAuthToken(token: string | null): void {
  _token = token
}

export async function authFetch(
  url: string,
  init: RequestInit = {},
): Promise<Response> {
  // Ask Amplify for the current valid token. In the common case (token not expired)
  // this returns immediately from memory. When the access token has expired Amplify
  // uses the stored refresh token to obtain a new one before returning.
  let token = _token
  try {
    const session = await fetchAuthSession()
    const fresh = session.tokens?.accessToken?.toString() ?? null
    if (fresh) {
      token = fresh
      _token = fresh // keep module-level store in sync
    }
  } catch {
    // fetchAuthSession throws when the user is fully logged out or Amplify is not
    // yet configured — fall through and use whatever _token currently holds.
  }

  const headers = new Headers(init.headers)
  if (token) {
    headers.set('Authorization', `Bearer ${token}`)
  }
  const response = await fetch(url, { ...init, headers })
  // Check for auth token expiry
  if (response.status === 401) {
    // Token invalid or expired — clear auth state and redirect to login
    setAuthToken(null)
    window.location.href = '/login'
  }
  return response
}
